import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { CARDS, boardClock } from "@/data/projectsBoard";
import { useBoard } from "@/hooks/useBoard";
import { ProjectsBoard } from "./ProjectsBoard";
import { ProjectWorkshop } from "./Workshop";
import { ProjectDetail } from "./ProjectDetail";
import { Card, Label, StateWord } from "@/design/primitives";
import { AuthGate, StartHere, AddWorkBar } from "@/screens/cc";
import { recordAsk, recordContext } from "@/lib/queueRows";

const SearchBar = ({ value, onChange, count }) => (
  <div className="flex items-center gap-3" data-testid="projects-search">
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={`Search ${count} projects by name…`}
      className="h-10 w-full rounded-md border border-os-border bg-white px-3 text-[0.875rem] text-os-ink outline-none focus:border-os-border-strong"
      data-testid="projects-search-input"
    />
    {value && (
      <button type="button" onClick={() => onChange("")} className="font-mono text-[0.6875rem] text-os-ink-3 hover:text-os-ink">
        clear
      </button>
    )}
  </div>
);

// Recovery lanes (Jeff 2026-10-21): stalled (no air in 14+ days), failed/retrying,
// and drafts — the projects that need a hand, surfaced instead of buried.
const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);
const RecoveryLanes = ({ cards, onOpen }) => {
  const draft = cards.filter((c) => ["INTAKE", "PLAN"].includes(c.rawState));
  const postponed = cards.filter((c) => c.rawState === "TABLED");
  const stalled = cards.filter((c) => {
    if (["EXECUTE", "TEST"].indexOf(c.rawState) < 0) return false;
    const d = daysSince(c.lastKitActionAt || c.updated_at || c.updatedAt);
    return d != null && d >= 14;
  });
  const failed = cards.filter((c) => String(c.blockersLine || c.blockers || "").toLowerCase().includes("fail")
    || String(c.next || "").toLowerCase().includes("failed"));
  if (!draft.length && !stalled.length && !failed.length) return null;
  return (
    <div className="flex flex-col gap-2" data-testid="projects-recovery">
      <Label as="h2">Needs a hand — drafts · stalled · failed · set aside</Label>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
        {[
          { key: "drafts", tone: "amber", word: "Drafts", rows: draft },
          { key: "stalled", tone: "amber", word: "Stalled 14+ days", rows: stalled },
          { key: "failed", tone: "red", word: "Failed / retrying", rows: failed },
          { key: "postponed", tone: "muted", word: "Set aside", rows: postponed },
        ].map(({ key, tone, word, rows }) => rows.length === 0 ? null : (
          <Card key={key} className="px-4 py-3" data-testid={`projects-lane-${key}`}>
            <div className="flex items-baseline justify-between gap-2">
              <p className="font-mono text-[0.6875rem] text-os-ink-3">{word.toUpperCase()}</p>
              <StateWord tone={rows.length ? tone : "muted"}>{rows.length || "none"}</StateWord>
            </div>
            <ul className="mt-2 flex flex-col gap-1.5">
              {rows.slice(0, 4).map((c) => (
                <li key={c.slug}>
                  <button type="button" onClick={() => onOpen(c.slug)} className="w-full truncate text-left text-[0.8125rem] text-os-ink hover:underline underline-offset-2">
                    {c.title}
                  </button>
                </li>
              ))}
              {rows.length === 0 && <li className="text-[0.8125rem] text-os-ink-3">Nothing here right now.</li>}
            </ul>
          </Card>
        ))}
      </div>
    </div>
  );
};


// ── Waiting on you (council round 2026-10-21) ─────────────────────────────
// One row per project that's blocked on Jeff: what it is, the exact ask,
// Kit's latest line, and the approve button right there. Phase-gated projects
// get their gate released from the same row. No hunting through cards.
//
// Jeff's bug (2026-10-23): this deck had the same face as the Needs Me
// queue — every TEST/PLAN row got an Approve button and a blockersLine
// headline, even when the board said "Nothing is blocking this right now"
// and the record's own next step was the close-out. So the deck reads the
// SAME rules (lib/queueRows.js) the queue does: the context is the record's
// real next step, and the button appears only when an approval is really
// being asked.
const WaitingOnDeck = ({ rows, board, onFlash, onOpen, desktop }) => {
  const pending = board.pending;
  return (
    <section data-testid="projects-waiting-deck" className="flex flex-col gap-2">
      <Label as="h2">Waiting on you — {rows.length}</Label>
      <div className="flex flex-col gap-2">
        {rows.map((r) => {
          const p = r.payload || {};
          const runner = p.runner || null;
          const gate = runner?.phases?.findIndex((ph) => ph.gate && !ph.gate_approved);
          const isGate = runner && gate != null && gate >= 0;
          const busy = pending === r.slug;
          const lastLine = p.lastKitAction || p.next;
          // The same two questions the queue asks, in the same words.
          const ask = recordAsk(r.state, p);
          const context = recordContext(p, r.state);
          return (
            <Card key={r.slug} data-testid={`deck-row-${r.slug}`} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <StateWord tone="amber">{p.stateWord || "Waiting"}</StateWord>
                <button type="button" onClick={() => onOpen(r.slug)} className="min-w-0 truncate text-left text-[0.9375rem] font-semibold text-os-ink hover:underline underline-offset-2">
                  {p.title || r.slug}
                </button>
                <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">{p.brand}</span>
                {/* A button only when there is a real move for Jeff to make.
                    The close case IS an approval under the board's state
                    machine (TEST→CLOSE), so it gets a button too — one that
                    says "Close it out" instead of "Approve". A record with
                    no real ask gets a link to read it, never a button that
                    lies. */}
                {ask.kind === "gate" || ask.kind === "approve" || ask.kind === "close" ? (
                  <button
                    type="button"
                    data-testid={ask.kind === "close" ? `deck-close-${r.slug}` : `deck-approve-${r.slug}`}
                    disabled={busy}
                    onClick={async () => {
                      const res = isGate
                        ? await board.edit("phaseApproveGate", r.slug, { move: gate, note: "Go ahead." })
                        : await board.act("approve", r.slug);
                      if (res) {
                        if (isGate) onFlash("Gate cleared — Kit picks the phases back up.");
                        else if (ask.kind === "close") onFlash("Closed out — Kit does the archive pass, docs and ledger.");
                        else onFlash("Approved — it's moving. Kit finishes the wrap-up from here.");
                      }
                    }}
                    className="ml-auto rounded-md border border-os-border-strong bg-os-ink px-3 py-1.5 font-mono text-[0.71875rem] text-white hover:opacity-90 disabled:opacity-50"
                  >
                    {busy ? "Moving it…" : isGate ? "Approve & continue" : ask.label}
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid={`deck-open-${r.slug}`}
                    onClick={() => onOpen(r.slug)}
                    className="ml-auto rounded-md border border-os-border-strong px-3 py-1.5 font-mono text-[0.71875rem] text-os-ink hover:opacity-90"
                  >
                    {ask.label}
                  </button>
                )}
              </div>
              <p data-testid={`deck-ask-${r.slug}`} className="mt-1 text-[0.875rem] font-medium text-os-ink">
                {context || "Your pass moves this forward"}
              </p>
              {lastLine && (
                <p data-testid={`deck-last-${r.slug}`} className="mt-0.5 truncate text-[0.8125rem] leading-[1.45] text-os-ink-3">
                  {isGate ? `Phase ${gate + 1} holds at the checkpoint — ${lastLine}` : lastLine}
                </p>
              )}
            </Card>
          );
        })}
      </div>
    </section>
  );
};

export default function ProjectsBoardPage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const clock = useMemo(() => boardClock(), []);
  const board = useBoard("projects");
  const [openSlug, setOpenSlug] = useState(null);
  const [flash, setFlash] = useState(null);
  const [search, setSearch] = useState("");

  const liveItems = board.items || [];
  const liveProjects = liveItems.filter((r) => r.kind === "project");
  const liveIdeas = liveItems.filter((r) => r.kind === "idea" && r.state !== "ARCHIVED");

  const cards = useMemo(() => {
    if (!board.live) return CARDS;
    return [...liveProjects]
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .map((r) => {
        const p = r.payload || {};
        const approved = (p.jeff || {}).approved;
        return {
          ...p,
          slug: r.slug,
          rawState: r.state,
          stateWord: approved ? "Approved — wrapping up" : p.stateWord,
          next: approved
            ? "Next: Kit finishes the wrap-up — archive pass, notes, and the work ledger"
            : p.next,
          blockersLine: approved ? "Nothing waiting on you anymore" : p.blockersLine,
        };
      });
  }, [board.live, liveProjects]);

  // The deck is the "waiting on you" list — so a row only belongs here if
  // something is actually being asked of Jeff. A TEST record with no
  // blocker, no next step and no gate asks nothing (Jeff, 10-23), so it is
  // not in this list at all.
  const waiting = liveItems.filter(
    (r) =>
      r.kind === "project" &&
      (r.state === "TEST" || r.state === "PLAN") &&
      !(r.payload?.jeff || {}).approved &&
      Boolean(recordContext(r.payload || {}, r.state)),
  );
  const firstWait = waiting[0];
  // The start-here line asks for what the first row actually needs. A record
  // whose next step is the close-out is not an approval (Jeff, 10-23), so
  // the line says that and offers no approve button.
  const firstAsk = firstWait ? recordAsk(firstWait.state, firstWait.payload || {}) : null;
  const firstIsApproval = firstAsk && (firstAsk.kind === "gate" || firstAsk.kind === "approve");
  const firstIsClose = firstAsk && firstAsk.kind === "close";
  const startHere = !board.live
    ? {
        tone: "muted",
        text: board.status === "needs-signin"
          ? "Sign in to work the board — your buttons appear the moment you're in"
          : "Actions are offline this visit — the board still shows the latest committed state",
      }
    : firstWait
      ? {
          tone: "amber",
          // The line asks for what the first row actually needs. A record
          // whose next step is the close-out is not an approval (Jeff,
          // 10-23) — the line says what the click does, and the button
          // says "Close it out".
          text: firstIsApproval
            ? `Approve the ${firstWait.payload?.title || "next piece of work"} — one click and the work moves`
            : firstIsClose
              ? `${firstWait.payload?.title || "The next piece of work"} is ready to close out — one click and Kit does the archive pass, docs and ledger`
              : `${firstWait.payload?.title || "The next piece of work"} is waiting on you — open it and see what it needs`,
          ...(firstIsApproval || firstIsClose
            ? {
                button: {
                  label:
                    board.pending === firstWait.slug
                      ? "Moving it…"
                      : firstIsClose
                        ? "Close it out"
                        : "Approve",
                  disabled: Boolean(board.pending),
                  onClick: async () => {
                    const res = await board.act("approve", firstWait.slug);
                    if (res) {
                      setFlash(
                        firstIsClose
                          ? "Closed out — Kit does the archive pass, docs and ledger."
                          : "Approved — it's moving. Kit finishes the wrap-up from here.",
                      );
                    }
                  },
                },
              }
            : {}),
        }
      : liveIdeas.length
        ? { tone: "muted", text: "Nothing waits on your approval — Kit is executing. New ideas welcome below." }
        : { tone: "green", text: "All clear — nothing needs you here right now." };

  const openItem = board.live
    ? (() => {
        const row = liveItems.find((r) => r.slug === openSlug);
        if (!row) return null;
        if (row.kind === "idea") return null;
        const p = row.payload || {};
        const approved = (p.jeff || {}).approved;
        return {
          ...p,
          slug: row.slug,
          rawState: row.state,
          stateWord: approved ? "Approved — wrapping up" : p.stateWord,
        };
      })()
    : null;

  const close = () => setOpenSlug(null);

  const renderBoard = (children) => (
    <div data-testid="projects-live-page" className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        {children}
      </div>
    </div>
  );

  if (board.status === "needs-signin") {
    return renderBoard(
      <>
        <AuthGate />
        <ProjectsBoard cards={CARDS} clock={clock} variant={desktop ? "desktop" : "mobile"} board={board} />
      </>,
    );
  }

  return renderBoard(
    <>
      <StartHere {...startHere} testId="projects-start-here" />
      {flash && (
        <p data-testid="projects-flash" className="text-[0.875rem] font-medium text-os-green">
          {flash}
        </p>
      )}
      {board.live ? (
        <>
          {/* Council round 2026-10-21: decisions first — the deck, then work in
              motion, then everything that waits for a rare visit. */}
          {waiting.length > 0 && (
            <WaitingOnDeck
              rows={waiting}
              board={board}
              onFlash={setFlash}
              onOpen={setOpenSlug}
              desktop={desktop}
            />
          )}
          <ProjectsBoard
            cards={search ? cards.filter((c) => (c.title || "").toLowerCase().includes(search.toLowerCase())) : cards}
            clock={clock}
            variant={desktop ? "desktop" : "mobile"}
            board={board}
          />
          <RecoveryLanes cards={cards} onOpen={setOpenSlug} />
          <SearchBar value={search} onChange={setSearch} count={cards.length} />
          <ProjectWorkshop flash={setFlash} desktop={desktop} />
          <AddWorkBar />
        </>
      ) : (
        <>
          <ProjectsBoard cards={CARDS} clock={clock} variant={desktop ? "desktop" : "mobile"} board={board} />
          <p className="px-1 font-mono text-[0.6875rem] text-os-ink-3">
            Actions are offline this visit — the board shows the latest committed state.
          </p>
        </>
      )}
      {openItem && (
        <ProjectDetail
          card={openItem}
          variant={desktop ? "desktop" : "mobile"}
          onClose={close}
          editor={board.live ? { board, item: liveItems.find((r) => r.slug === openSlug) } : null}
        />
      )}
    </>,
  );
}
